import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, Stack, Alert, CircularProgress, ToggleButton,
  ToggleButtonGroup, Tooltip, Tabs, Tab, Paper, Grid, TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import LoginIcon from "@mui/icons-material/Login";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";

const STATUS_CHIP = (t) => ({
  pending: { label: t("admin.statusPending"), color: "warning" },
  active: { label: t("admin.statusActive"), color: "success" },
  suspended: { label: t("admin.statusSuspended"), color: "default" },
});

const ROLE_COLOR = {
  superadmin: "secondary",
  master: "primary",
  user: "default",
  guest: "default",
};

function StatCard({ label, value, color }) {
  return (
    <Paper sx={{ p: 2, textAlign: "center" }}>
      <Typography variant="h4" fontWeight={700} sx={{ color: color || "text.primary" }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function AdminWorkspaces() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchWorkspace } = useAuth();
  const statusChip = STATUS_CHIP(t);

  const [tab, setTab] = useState("salons");
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [workspaces, setWorkspaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [entering, setEntering] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, w] = await Promise.all([
        api.getAdminStats(),
        api.getAdminWorkspaces(filter === "all" ? undefined : filter),
      ]);
      setStats(s);
      setWorkspaces(w.workspaces || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.getAdminUsers(undefined, userQuery);
      setUsers(res.users || []);
    } catch (e) {
      setError(e.message);
    }
  }, [userQuery]);

  useEffect(() => {
    if (tab === "users") loadUsers();
  }, [tab, loadUsers]);

  const setStatus = async (id, status) => {
    try {
      await api.setWorkspaceStatus(id, status);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  // God входить у салон і керує ним як власник
  const manage = async (id) => {
    setEntering(id);
    try {
      await switchWorkspace(id);
      navigate("/master/dashboard");
    } catch (e) {
      setError(e.message);
      setEntering(null);
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("admin.platformTitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>
      )}

      {/* Зведення */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}><StatCard label={t("admin.salonsTotal")} value={stats.workspaces.total} /></Grid>
          <Grid item xs={6} md={3}><StatCard label={t("admin.statusPending")} value={stats.workspaces.pending} color="#e6912a" /></Grid>
          <Grid item xs={6} md={3}><StatCard label={t("admin.usersTotal")} value={stats.users.total} /></Grid>
          <Grid item xs={6} md={3}><StatCard label={t("admin.bookingsTotal")} value={stats.bookings} /></Grid>
        </Grid>
      )}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="salons" label={t("admin.tabSalons")} />
        <Tab value="users" label={t("admin.tabUsers")} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : tab === "salons" ? (
        <>
          <ToggleButtonGroup size="small" exclusive value={filter}
            onChange={(_e, v) => v && setFilter(v)} sx={{ mb: 2 }}>
            <ToggleButton value="all">{t("admin.filterAll")}</ToggleButton>
            <ToggleButton value="pending">{t("admin.statusPending")}</ToggleButton>
            <ToggleButton value="active">{t("admin.statusActive")}</ToggleButton>
            <ToggleButton value="suspended">{t("admin.statusSuspended")}</ToggleButton>
          </ToggleButtonGroup>

          {workspaces.length === 0 ? (
            <Typography color="text.secondary">{t("admin.empty")}</Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.salon")}</TableCell>
                    <TableCell>{t("admin.owner")}</TableCell>
                    <TableCell align="center">{t("admin.members")}</TableCell>
                    <TableCell align="center">{t("admin.servicesCol")}</TableCell>
                    <TableCell align="center">{t("admin.bookingsCol")}</TableCell>
                    <TableCell>{t("admin.status")}</TableCell>
                    <TableCell align="right">{t("admin.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workspaces.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{w.name}</Typography>
                        <Typography variant="caption" color="text.secondary">/{w.slug}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{w.ownerName}</Typography>
                        <Typography variant="caption" color="text.secondary">{w.ownerEmail || w.ownerPhone}</Typography>
                      </TableCell>
                      <TableCell align="center">{w.memberCount}</TableCell>
                      <TableCell align="center">{w.serviceCount}</TableCell>
                      <TableCell align="center">{w.bookingCount}</TableCell>
                      <TableCell>
                        <Chip size="small" label={statusChip[w.status]?.label || w.status}
                          color={statusChip[w.status]?.color || "default"}
                          variant={w.status === "suspended" ? "outlined" : "filled"} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                          <Tooltip title={t("admin.manage")}>
                            <Button size="small" variant="contained" startIcon={<LoginIcon />}
                              disabled={entering === w.id} onClick={() => manage(w.id)}>
                              {t("admin.manage")}
                            </Button>
                          </Tooltip>
                          {w.status !== "active" && (
                            <Button size="small" color="success" startIcon={<CheckCircleIcon />}
                              onClick={() => setStatus(w.id, "active")}>{t("admin.approve")}</Button>
                          )}
                          {w.status !== "suspended" && (
                            <Button size="small" color="inherit" startIcon={<PauseCircleIcon />}
                              onClick={() => setStatus(w.id, "suspended")}>{t("admin.suspend")}</Button>
                          )}
                          {w.status !== "pending" && (
                            <Button size="small" color="inherit" startIcon={<ReplayIcon />}
                              onClick={() => setStatus(w.id, "pending")}>{t("admin.toPending")}</Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      ) : (
        <>
          <TextField size="small" fullWidth sx={{ mb: 2, maxWidth: 360 }}
            placeholder={t("admin.searchUsers")} value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)} />
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.name")}</TableCell>
                  <TableCell>{t("admin.role")}</TableCell>
                  <TableCell>{t("admin.contact")}</TableCell>
                  <TableCell>{t("admin.memberships")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={u.role} color={ROLE_COLOR[u.role] || "default"} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{u.phone}</Typography>
                      {u.email && <Typography variant="caption" color="text.secondary">{u.email}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(u.memberships || []).length === 0 ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          u.memberships.map((m) => (
                            <Chip key={m.workspaceId} size="small" variant="outlined"
                              label={`${m.name}: ${m.role}`} />
                          ))
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
